import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, CheckCircle2, Plus, Minus, Microscope, ShieldCheck, Zap, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-2122">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-2123" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-2124">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-2125">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-2126">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-2127">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-2128" /> : <Plus className="w-4 h-4" data-id="element-2129" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-2130">
        <p className="text-gray-600 leading-relaxed" data-id="element-2131">{answer}</p>
      </div>
    </div>;
}
export function RootCanalLondonPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Коли потрібно лікувати канали?',
    a: 'При запаленні нерва (пульпіт) або інфекції в корені (періодонтит). Симптоми: сильний біль, чутливість до гарячого/холодного, набряк ясен, біль при накусуванні або нічний біль.'
  }, {
    q: 'Чи боляче лікувати канали?',
    a: 'Ні. Лікування проводиться під ефективною місцевою анестезією, тому сама процедура безболісна. Після лікування можливий тимчасовий дискомфорт протягом кількох днів.'
  }, {
    q: 'Скільки коштує лікування каналів у Лондоні?',
    a: 'Вартість залежить від кількості каналів у зубі та складності випадку. Після огляду та рентгену ми надаємо точний план лікування та кошторис.'
  }, {
    q: 'Скільки візитів потрібно?',
    a: 'Іноді лікування можна завершити за 1 візит. У складніших випадках або при наявності інфекції може знадобитися 2 візити. Це визначається після діагностики.'
  }, {
    q: 'Чи потрібна коронка після лікування каналів?',
    a: 'У більшості випадків так, особливо для жувальних зубів. Зуб після видалення нерва стає крихким, і коронка захищає його від розколу та подовжує термін служби.'
  }, {
    q: 'Чи можна врятувати зуб, якщо сильно болить?',
    a: 'У більшості випадків так. Сучасна ендодонтія дозволяє врятувати навіть сильно зруйновані або інфіковані зуби. Чим раніше ви звернетесь, тим більше шансів зберегти зуб.'
  }, {
    q: 'Чи робите термінові візити при гострому болю?',
    a: "Так, ми розуміємо, що зубний біль не можна терпіти. Зв'яжіться з нами якнайшвидше — ми постараємося знайти найближчий доступний час."
  }, {
    q: 'Де знаходиться клініка?',
    a: '360–364 City Road, London EC1V 2PY, поруч з Angel Station (Islington).'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Root Canal Treatment',
    description: 'Endodontic treatment to save a tooth that is badly decayed or infected.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-2132">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-2133" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-2134" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-2135" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-white overflow-hidden" data-id="element-2136">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-2137" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-2138">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2139">
            <div className="space-y-6" data-id="element-2140">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-sm font-medium" data-id="element-2141">
                <span className="relative flex h-2 w-2" data-id="element-2142">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-2143"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" data-id="element-2144"></span>
                </span>
                Ендодонтія (лікування каналів)
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-2145">
                Лікування каналів у Лондоні –{' '}
                <span className="text-teal-600" data-id="element-2146">Dentico Clinic</span>
                <br data-id="element-2147" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-2148">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-2149">
                Безболісне лікування кореневих каналів під мікроскопом. Рятуємо
                зуби від видалення. Консультація українською мовою.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-2150">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-2151">
                  <Microscope className="w-4 h-4 text-teal-600" data-id="element-2152" />
                  Лікування під мікроскопом
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-2153">
                  <Zap className="w-4 h-4 text-yellow-500" data-id="element-2154" />
                  Без болю
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-2155">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-2156" />
                  Українська мова
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-2157">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2158">
                  <Button size="lg" className="w-full gap-2 shadow-lg shadow-teal-600/20" data-id="element-2159">
                    <Calendar className="w-5 h-5" data-id="element-2160" />
                    Записатись на прийом
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2161">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-2162">
                    <MessageCircle className="w-5 h-5 text-green-600" data-id="element-2163" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-2164">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-2165"></div>
              <img src="https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1200&q=80" alt="Лікування каналів - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-2166" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT IS ROOT CANAL */}
      <section className="py-16 bg-white" data-id="element-2167">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2168">
          <div className="text-center mb-10" data-id="element-2169">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2170">
              Що таке лікування каналів?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-2171">
              Лікування кореневих каналів (ендодонтія) — це процедура,
              спрямована на порятунок зуба, який був сильно пошкоджений карієсом
              або інфекцією. Коли бактерії проникають всередину зуба до нерва
              (пульпи), це викликає запалення та біль. Якщо не провести
              лікування вчасно, інфекція може поширитися на кістку і призвести
              до втрати зуба.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4" data-id="element-2172">
              Сучасна ендодонтія дозволяє очистити канали від інфекції,
              запломбувати їх та відновити зуб, зберігаючи його функціональність
              на довгі роки. Це єдина альтернатива видаленню зуба при глибокому
              ураженні.
            </p>
          </div>
        </div>
      </section>

      {/* 3. PROCESS */}
      <section className="py-16 bg-gray-50" data-id="element-2173">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2174">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-2175">
            Як проходить лікування
          </h2>
          <div className="grid md:grid-cols-5 gap-4" data-id="element-2176">
            {[{
            step: '01',
            title: 'Діагностика',
            desc: 'Рентген-знімок для оцінки стану каналів.'
          }, {
            step: '02',
            title: 'Анестезія',
            desc: 'Повне знеболення для комфортного лікування.'
          }, {
            step: '03',
            title: 'Очищення',
            desc: 'Видалення інфікованих тканин та обробка каналів.'
          }, {
            step: '04',
            title: 'Пломбування',
            desc: 'Герметичне заповнення каналів спеціальним матеріалом.'
          }, {
            step: '05',
            title: 'Відновлення',
            desc: 'Встановлення пломби або коронки для захисту зуба.'
          }].map((item, idx) => <Card key={idx} className="p-5 relative overflow-hidden h-full" data-id="element-2177">
                <div className="text-5xl font-bold text-teal-50 absolute -top-2 -right-2 select-none" data-id="element-2178">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-2179">
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2180">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-2181">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. MODERN APPROACH */}
      <section className="py-16 bg-white" data-id="element-2182">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2183">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2184">
            <div data-id="element-2185">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2186">
                Сучасний підхід до лікування
              </h2>
              <ul className="space-y-4" data-id="element-2187">
                {['Використання дентального мікроскопа для точності', 'Цифрова рентген-діагностика (мінімальне опромінення)', 'Ефективна анестезія (лікування без болю)', 'Можливість лікування за один візит', 'Збереження навіть складних зубів'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-2188">
                    <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" data-id="element-2189" />
                    <span className="text-gray-700 text-lg" data-id="element-2190">{item}</span>
                  </li>)}
              </ul>
              <div className="mt-8" data-id="element-2191">
                <Link to="/ua/contact" data-id="element-2192">
                  <Button size="lg" className="gap-2" data-id="element-2193">
                    <Calendar className="w-5 h-5" data-id="element-2194" />
                    Записатись на консультацію
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-2195">
              <img src="https://images.unsplash.com/photo-1609840114035-3c981b782dfe?auto=format&fit=crop&w=1200&q=80" alt="Сучасне лікування каналів" className="absolute inset-0 w-full h-full object-cover" data-id="element-2196" />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS */}
      <section className="py-16 bg-teal-50" data-id="element-2197">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2198">
          <div className="text-center mb-12" data-id="element-2199">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2200">
              Чому варто лікувати канали у нас
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2201">
            {[{
            title: 'Збереження зуба',
            desc: 'Ми боремося за кожен зуб, щоб уникнути видалення та імплантації.'
          }, {
            title: 'Без болю',
            desc: 'Сучасні анестетики гарантують комфорт під час процедури.'
          }, {
            title: 'Українська мова',
            desc: 'Ви точно зрозумієте діагноз та план лікування.'
          }, {
            title: 'Досвідчені лікарі',
            desc: 'Спеціалісти з ендодонтії, які працюють зі складними випадками.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white text-center" data-id="element-2202">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-4 mx-auto" data-id="element-2203">
                  <ShieldCheck className="w-6 h-6" data-id="element-2204" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2205">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-2206">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className="py-12 bg-teal-600" data-id="element-2207">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-2208">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4" data-id="element-2209">
            Не відкладайте лікування — збережіть зуб
          </h2>
          <p className="text-teal-100 mb-6" data-id="element-2210">
            Чим раніше ви звернетесь, тим більше шансів зберегти зуб без
            ускладнень.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-2211">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-2212">
              <Button size="lg" className="gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-2213">
                <Calendar className="w-5 h-5" data-id="element-2214" />
                Записатись онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-2215">
              <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" data-id="element-2216">
                <MessageCircle className="w-5 h-5" data-id="element-2217" />
                WhatsApp
              </Button>
            </a>
            <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" onClick={() => setIsCallbackOpen(true)} data-id="element-2218">
              <Phone className="w-5 h-5" data-id="element-2219" />
              Замовити дзвінок
            </Button>
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-2220">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2221">
          <div className="text-center mb-10" data-id="element-2222">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2223">
              Часті запитання про лікування каналів
            </h2>
          </div>
          <div className="space-y-2" data-id="element-2224">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-2225" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-2226">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2227">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2228">
            Дивіться також
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-2229">
            <Link to="/ua/ukrainskyi-stomatolog-london" data-id="element-2230">
              <Button variant="outline" size="sm" data-id="element-2231">
                Український стоматолог
              </Button>
            </Link>
            <Link to="/ua/implanty-london" data-id="element-2232">
              <Button variant="outline" size="sm" data-id="element-2233">
                Імплантація
              </Button>
            </Link>
            <Link to="/ua/veneers-london" data-id="element-2234">
              <Button variant="outline" size="sm" data-id="element-2235">
                Вініри
              </Button>
            </Link>
            <Link to="/ua/emergency-dentist-london" data-id="element-2236">
              <Button variant="outline" size="sm" data-id="element-2237">
                Невідкладна допомога
              </Button>
            </Link>
            <Link to="/ua/services/crowns" data-id="element-2238">
              <Button variant="outline" size="sm" data-id="element-2239">
                Коронки
              </Button>
            </Link>
            <Link to="/ua/prices" data-id="element-2240">
              <Button variant="outline" size="sm" data-id="element-2241">
                Ціни
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-16 bg-teal-900" data-id="element-2242">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-2243">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2244">
            Не терпіть зубний біль
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-2245">
            Збережіть свій зуб сьогодні. Запишіться на консультацію.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2246">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2247">
              <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-2248">
                <Calendar className="w-5 h-5" data-id="element-2249" />
                Записатись онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2250">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-2251">
                <MessageCircle className="w-5 h-5" data-id="element-2252" />
                WhatsApp
              </Button>
            </a>
            <div className="w-full sm:w-auto" data-id="element-2253">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" onClick={() => setIsCallbackOpen(true)} data-id="element-2254">
                <Phone className="w-5 h-5" data-id="element-2255" />
                Замовити дзвінок
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}